import type { RootState } from "../state";
import { emitter } from "@/utils/emitter/mitt";
import {
  createUniversalWebAudioPlayer,
  type AudioItem,
  type UniversalWebAudioPlayer
} from "./universalWebAudio";

interface UniversalWAState {
  player: UniversalWebAudioPlayer;
}

type Store = RootState & { [key: string]: any };

function getWAState(store: Store, roomId: string): UniversalWAState | null {
  const state = store.roomStates.get(roomId);
  if (!state) return null;
  return (state as any).__universalWA__ || null;
}

function setWAState(store: Store, roomId: string, wa: UniversalWAState) {
  const state = store.roomStates.get(roomId);
  if (!state) return;
  (state as any).__universalWA__ = wa;
}

function ensureListeners(this: Store, roomId: string) {
  const state = this.roomStates.get(roomId);
  if (!state) return;

  if (!(state as any).__universalBoundaryHandler) {
    const boundaryHandler = (event: any) => {
      if (event.roomId === roomId) {
        console.log(
          `[UniversalWA] 收到边界切换事件，执行产品切换: ${event.targetPid}`
        );
        const storeState = this.roomStates.get(roomId);
        try {
          if (this.executePendingProductSwitch) {
            this.executePendingProductSwitch(roomId);
          }
          if (storeState) {
            (storeState as any).__awaitUniversalBoundary = false;
          }
        } catch (error) {
          console.error("[UniversalWA] 边界切换处理失败:", error);
        }
      }
    };
    emitter.on("universalWebAudio:boundarySwitch", boundaryHandler);
    (state as any).__universalBoundaryHandler = boundaryHandler;
  }

  if (!(state as any).__universalTargetReadyHandler) {
    const targetReadyHandler = (event: any) => {
      if (event.roomId === roomId) {
        const storeState = this.roomStates.get(roomId);
        if (!storeState || !storeState.pendingProductSwitch) return;

        let stillPlaying = false;
        try {
          const universalPlayer = (storeState as any).universalWebAudioPlayer;
          if (
            universalPlayer &&
            typeof universalPlayer.getStatus === "function"
          ) {
            stillPlaying = !!universalPlayer.getStatus().isPlaying;
          }
        } catch {}

        if (stillPlaying) {
          try {
            console.log(
              `[UniversalWA] 目标音频已就绪，等待当前句播放完成 room=${roomId}`
            );
          } catch {}

          try {
            (storeState as any).__awaitUniversalBoundary = true;
          } catch {}
          return;
        }

        try {
          console.log(
            `[UniversalWA] 收到目标音频就绪事件，执行待切换: ${event.targetPid}`
          );
          if (event.queueSnapshot) {
            console.log(
              `[queue-debug] targetReady snapshot roomId=${roomId}`,
              event.queueSnapshot
            );
          }
        } catch {}
        try {
          if (this.executePendingProductSwitch) {
            this.executePendingProductSwitch(roomId);
          }
          (storeState as any).__awaitUniversalBoundary = false;
        } catch (error) {
          console.error("[UniversalWA] targetReady 处理失败:", error);
        }
      }
    };
    emitter.on("universalWebAudio:targetReady", targetReadyHandler);
    (state as any).__universalTargetReadyHandler = targetReadyHandler;
  }

  if (!(state as any).__universalSyncHandler) {
    const syncHandler = (event: any) => {
      if (event.roomId !== roomId) return;
      const storeState = this.roomStates.get(roomId);
      if (!storeState) return;

      const snapshot = Array.isArray(event.queueSnapshot)
        ? event.queueSnapshot
        : [];
      const awaitingBoundary = Boolean(
        (storeState as any).__awaitUniversalBoundary
      );

      const existingMap = new Map(
        (storeState.queue || []).map((item: any) => [item?.id, item])
      );

      const newQueue = snapshot.map((entry: any) => {
        const existing = existingMap.get(entry.id) || {};
        const merged: any = {
          ...existing,
          id: entry.id,
          text: entry.text ?? existing.text ?? "",
          productId: entry.productId ?? existing.productId ?? null,
          voiceNo: entry.voiceNo ?? existing.voiceNo ?? null,
          status: entry.status ?? existing.status ?? "pending"
        };

        if (merged.status === "played") {
          merged.playedCharCount = merged.text?.length ?? 0;
        } else if (merged.status !== "playing") {
          merged.playedCharCount = merged.playedCharCount ?? 0;
        }

        return { ...merged };
      });

      storeState.queue = newQueue;

      const hasQueue = newQueue.length > 0;
      let nextIndex = Number(event.currentIndex ?? 0);
      if (!Number.isFinite(nextIndex) || nextIndex < 0) nextIndex = 0;
      if (hasQueue) {
        nextIndex = Math.min(nextIndex, newQueue.length - 1);
      } else {
        nextIndex = 0;
      }

      storeState.currentIndex = nextIndex;
      storeState.isPlaying = hasQueue ? Boolean(event.isPlaying) : false;

      if (awaitingBoundary && newQueue.length > 0) {
        try {
          (storeState as any).__skipUniversalPlayId = newQueue[0]?.id || null;
        } catch {}
      } else if (newQueue.length === 0) {
        try {
          (storeState as any).__skipUniversalPlayId = null;
        } catch {}
      }

      if (awaitingBoundary) {
        try {
          (storeState as any).__awaitUniversalBoundary = false;
        } catch {}
      }
    };

    emitter.on("universalWebAudio:syncQueue", syncHandler);
    (state as any).__universalSyncHandler = syncHandler;
  }
}

export async function ensureUniversalWebAudio(this: Store, roomId: string) {
  if (!roomId || !this.roomStates.has(roomId)) {
    throw new Error(`Room ${roomId} not found`);
  }
  const state = this.roomStates.get(roomId);
  if (!state) {
    throw new Error(`Room ${roomId} state missing`);
  }

  const existed = getWAState(this, roomId);
  if (existed?.player) {
    ensureListeners.call(this, roomId);
    return existed;
  }

  // 初始化全局标志，用于audioUnified.ts中的检测
  const g = window as any;
  if (!g.__universalWebAudioRooms) {
    g.__universalWebAudioRooms = new Set();
  }
  g.__universalWebAudioRooms.add(roomId);
  console.log(`[UniversalWA] 房间 ${roomId} 已标记为使用UniversalWebAudio`);

  const player = createUniversalWebAudioPlayer(roomId);

  // 添加初始化日志
  console.log(`[UniversalWA] 开始初始化播放器 roomId=${roomId}`);
  try {
    await player.initialize();
    console.log(`[UniversalWA] 播放器初始化成功 roomId=${roomId}`);
  } catch (error) {
    console.error(`[UniversalWA] 播放器初始化失败 roomId=${roomId}:`, error);
    throw error;
  }

  // 监听边界切换事件
  ensureListeners.call(this, roomId);

  player.setProgressCallback((idx, progress) => {
    const storeState = this.roomStates.get(roomId);
    if (!storeState) return;

    const status = player.getStatus();
    const total = status.totalItems;
    const currentIdx = Math.min(
      typeof status.currentIndex === "number" ? status.currentIndex : idx,
      storeState.queue.length > 0 ? storeState.queue.length - 1 : 0
    );

    storeState.currentIndex = Math.max(0, currentIdx);
    storeState.isPlaying = true;

    try {
      const current = storeState.queue[storeState.currentIndex];
      if (current) {
        const totalChars = current.text?.length || 0;
        current.status = "playing";
        current.playedCharCount = Math.floor(progress * totalChars);
        (current as any).audioTimeProgress = progress;
        const cloned = [...storeState.queue];
        cloned[storeState.currentIndex] = { ...current } as any;
        storeState.queue = cloned;

        try {
          (storeState as any).__universalCurrentId = current.id;
        } catch {}
      }
    } catch {
      // 若播放过程中同步失败忽略，保持原先兜底逻辑
    }

    if (this.currentRoomId === roomId) {
      storeState.progress = progress;
      emitter.emit("audioProgress", { roomId, progress });
    }
  });

  player.setCompleteCallback(() => {
    const storeState = this.roomStates.get(roomId);
    if (!storeState) return;

    const finishedIdx = Math.max(
      0,
      Math.min(storeState.currentIndex, storeState.queue.length - 1)
    );
    const finished = storeState.queue[finishedIdx];
    if (finished) {
      finished.status = "played";
      finished.playedCharCount = finished.text?.length || 0;
      try {
        const cloned = [...storeState.queue];
        cloned[finishedIdx] = { ...finished } as any;
        storeState.queue = cloned;
      } catch {}
    }

    const nextIndex = finishedIdx + 1;
    if (nextIndex >= storeState.queue.length) {
      storeState.currentIndex = nextIndex;
      storeState.isPlaying = false;
    } else {
      storeState.currentIndex = nextIndex;
      storeState.isPlaying = true;
    }

    try {
      (storeState as any).__universalCurrentId = null;
    } catch {}
  });

  player.setErrorCallback(error => {
    console.warn(`[UniversalWA] playback error`, error);
  });

  const waState: UniversalWAState = { player };
  setWAState(this, roomId, waState);
  (state as any).universalWebAudioPlayer = player;
  return waState;
}

export async function feedUniversalWebAudio(
  this: Store,
  roomId: string,
  legacyItem: {
    id: string;
    text: string;
    audioUrl: string;
    voiceNo?: number;
    productId?: string | null;
    commentId?: string | null;
  }
) {
  if (!legacyItem?.id || !legacyItem.audioUrl) return;

  console.log(
    `[UniversalWA] feedUniversalWebAudio 开始 roomId=${roomId}, id=${legacyItem.id}`
  );

  try {
    const state = this.roomStates.get(roomId);
    if (state) {
      const snapshot = (state.queue || [])
        .slice(0, 5)
        .map((q: any, idx: number) => ({
          idx,
          id: q?.id,
          voiceNo: q?.voiceNo,
          pid: q?.productId || null,
          status: q?.status,
          version: q?.version
        }));
      console.log(
        `[queue-debug] feed-before roomId=${roomId} len=${state.queue?.length ?? 0}`,
        snapshot
      );
    }
  } catch (error) {
    console.warn("[queue-debug] feed-before snapshot error", error);
  }

  const waState = await ensureUniversalWebAudio.call(this, roomId);
  const player = waState.player;

  console.log(`[UniversalWA] 获取到播放器实例, player=${!!player}`);

  const audioItem: AudioItem = {
    id: legacyItem.id,
    text: legacyItem.text || "",
    audioUrl: legacyItem.audioUrl,
    productId: legacyItem.productId || undefined,
    voiceNo: legacyItem.voiceNo,
    duration: undefined,
    commentId: legacyItem.commentId || null
  };

  try {
    if (Number(legacyItem.voiceNo) === -1) {
      await player.addUrgentNext(audioItem);
    } else {
      await player.addToPlaylist(audioItem);
    }

    const status = player.getStatus();
    if (!status.isPlaying) {
      await player.play();
      // 设置标记避免重复播放
      const state = this.roomStates.get(roomId);
      if (state) {
        (state as any).__skipUniversalPlayId = legacyItem.id;
      }
    }
  } catch (error) {
    console.warn(`[UniversalWA] feed failed: ${legacyItem.id}`, error);
  }

  try {
    const state = this.roomStates.get(roomId);
    if (state) {
      const snapshot = (state.queue || [])
        .slice(0, 5)
        .map((q: any, idx: number) => ({
          idx,
          id: q?.id,
          voiceNo: q?.voiceNo,
          pid: q?.productId || null,
          status: q?.status,
          version: q?.version
        }));
      console.log(
        `[queue-debug] feed-after roomId=${roomId} len=${state.queue?.length ?? 0}`,
        snapshot
      );
    }
  } catch (error) {
    console.warn("[queue-debug] feed-after snapshot error", error);
  }
}

// 清理UniversalWebAudio播放器
export function destroyUniversalWebAudio(this: Store, roomId: string) {
  const state = this.roomStates.get(roomId);
  if (!state) return;

  // 移除全局标志
  const g = window as any;
  if (g.__universalWebAudioRooms) {
    g.__universalWebAudioRooms.delete(roomId);
    console.log(`[UniversalWA] 房间 ${roomId} 已移除UniversalWebAudio标记`);
  }

  // 移除事件监听器
  const boundaryHandler = (state as any).__universalBoundaryHandler;
  if (boundaryHandler) {
    emitter.off("universalWebAudio:boundarySwitch", boundaryHandler);
    delete (state as any).__universalBoundaryHandler;
  }

  const targetReadyHandler = (state as any).__universalTargetReadyHandler;
  if (targetReadyHandler) {
    emitter.off("universalWebAudio:targetReady", targetReadyHandler);
    delete (state as any).__universalTargetReadyHandler;
  }

  const syncHandler = (state as any).__universalSyncHandler;
  if (syncHandler) {
    emitter.off("universalWebAudio:syncQueue", syncHandler);
    delete (state as any).__universalSyncHandler;
  }

  // 销毁播放器
  const waState = getWAState(this, roomId);
  if (waState?.player) {
    waState.player.destroy();
  }

  // 清理状态
  (state as any).__universalWA__ = null;
  (state as any).universalWebAudioPlayer = null;
}
